/**
 * GroundDetector - Handles ground detection and step-up logic for characters
 *
 * Detects when a character is standing on a surface and handles stepping up
 * small ledges (up to 1 unit height).
 */
export default class GroundDetector {
  // Maximum height a character can step up automatically
  static STEP_HEIGHT = 1.0;

  // Distance below feet to check for ground
  static GROUND_CHECK_DISTANCE = 0.15;

  // Small epsilon for floating point comparisons
  static EPSILON = 0.001;

  // Tolerance for considering character "on" ground (handles floating point drift)
  static GROUND_SNAP_TOLERANCE = 0.05;

  /**
   * Create a new ground detector
   * @param {CharacterCollisionSystem} collisionSystem - Reference to collision system
   */
  constructor(collisionSystem) {
    this.collisionSystem = collisionSystem;
  }

  /**
   * Check if a character is on the ground
   * @param {Character} character - The character to check
   * @returns {Object} { onGround: boolean, groundY: number|null }
   */
  checkGround(character) {
    const footY = character.y;
    const checkY = footY - GroundDetector.GROUND_CHECK_DISTANCE;

    // Create a thin box below the character's feet
    // Use wider vertical tolerance to catch ground even with floating point drift
    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const queryBox = {
      minX: character.x - halfWidth,
      minY: checkY,
      minZ: character.z - halfDepth,
      maxX: character.x + halfWidth,
      maxY: footY + GroundDetector.GROUND_SNAP_TOLERANCE,
      maxZ: character.z + halfDepth
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    let groundY = null;

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.) - can't stand on them
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidTop = cuboid.y + cuboid.height;

      // Check horizontal overlap
      if (this.horizontalOverlap(character, cuboid)) {
        // Ground must be within snap tolerance below feet, or very slightly above
        // (handles case where character is embedded slightly in ground)
        if (cuboidTop <= footY + GroundDetector.GROUND_SNAP_TOLERANCE &&
            cuboidTop >= footY - GroundDetector.GROUND_CHECK_DISTANCE) {
          // Track highest ground surface
          if (groundY === null || cuboidTop > groundY) {
            groundY = cuboidTop;
          }
        }
      }
    }

    return {
      onGround: groundY !== null,
      groundY: groundY
    };
  }

  /**
   * Check if character overlaps with cuboid in XZ plane
   * @param {Character} character - The character
   * @param {Cuboid} cuboid - The cuboid
   * @returns {boolean} True if overlapping
   */
  horizontalOverlap(character, cuboid) {
    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const charMinX = character.x - halfWidth;
    const charMaxX = character.x + halfWidth;
    const charMinZ = character.z - halfDepth;
    const charMaxZ = character.z + halfDepth;

    const cuboidMinX = cuboid.x;
    const cuboidMaxX = cuboid.x + cuboid.width;
    const cuboidMinZ = cuboid.z;
    const cuboidMaxZ = cuboid.z + cuboid.depth;

    return (
      charMinX < cuboidMaxX && charMaxX > cuboidMinX &&
      charMinZ < cuboidMaxZ && charMaxZ > cuboidMinZ
    );
  }

  /**
   * Check if character overlaps with cuboid in XZ plane at a specific position
   * @param {number} x - Character X position
   * @param {number} z - Character Z position
   * @param {number} width - Character width
   * @param {number} depth - Character depth
   * @param {Cuboid} cuboid - The cuboid
   * @returns {boolean} True if overlapping
   */
  horizontalOverlapAt(x, z, width, depth, cuboid) {
    const halfWidth = width / 2;
    const halfDepth = depth / 2;

    const charMinX = x - halfWidth;
    const charMaxX = x + halfWidth;
    const charMinZ = z - halfDepth;
    const charMaxZ = z + halfDepth;

    const cuboidMinX = cuboid.x;
    const cuboidMaxX = cuboid.x + cuboid.width;
    const cuboidMinZ = cuboid.z;
    const cuboidMaxZ = cuboid.z + cuboid.depth;

    return (
      charMinX < cuboidMaxX && charMaxX > cuboidMinX &&
      charMinZ < cuboidMaxZ && charMaxZ > cuboidMinZ
    );
  }

  /**
   * Try to step up onto a surface at a target position
   * @param {Character} character - The character
   * @param {number} targetX - Target X position
   * @param {number} targetZ - Target Z position
   * @returns {number|null} New Y position if step is valid, null if blocked
   */
  tryStepUp(character, targetX, targetZ) {
    const footY = character.y;
    const maxStepY = footY + GroundDetector.STEP_HEIGHT;

    // Query volume at target position up to step height
    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const queryBox = {
      minX: targetX - halfWidth,
      minY: footY,
      minZ: targetZ - halfDepth,
      maxX: targetX + halfWidth,
      maxY: maxStepY + character.height,
      maxZ: targetZ + halfDepth
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    // First pass: find valid step surfaces and collect potential blockers
    let highestStep = footY;
    let ceilingBlocked = false;
    const potentialBlockers = [];

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.) - can't step on them
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidTop = cuboid.y + cuboid.height;
      const cuboidBottom = cuboid.y;

      if (this.horizontalOverlapAt(targetX, targetZ, character.width, character.depth, cuboid)) {
        // This cuboid intersects our path
        const stepHeight = cuboidTop - footY;

        if (stepHeight > -GroundDetector.GROUND_SNAP_TOLERANCE && stepHeight <= GroundDetector.STEP_HEIGHT + GroundDetector.GROUND_SNAP_TOLERANCE) {
          // Valid step height - check if there's headroom above the step
          if (this.hasHeadroom(targetX, cuboidTop, targetZ, character)) {
            highestStep = Math.max(highestStep, cuboidTop);
          } else {
            // Can't step up due to ceiling directly above this surface
            ceilingBlocked = true;
          }
        } else if (stepHeight > GroundDetector.STEP_HEIGHT + GroundDetector.GROUND_SNAP_TOLERANCE) {
          // Too high to step onto - save for later check at stepped position
          potentialBlockers.push(cuboid);
        }
      }
    }

    // If blocked by ceiling on the only available step surface, fail
    if (ceilingBlocked && highestStep <= footY) {
      return null;
    }

    // Second pass: check if any tall cuboid would block us at the STEPPED position
    // This prevents false blocking when there's a wall behind a valid step
    for (const cuboid of potentialBlockers) {
      const cuboidTop = cuboid.y + (cuboid.collisionHeight || cuboid.height);
      const cuboidBottom = cuboid.y;

      // Would this cuboid intersect with character body at the stepped position?
      // Character at highestStep has body from highestStep to highestStep + height
      if (cuboidBottom < highestStep + character.height && cuboidTop > highestStep) {
        // This cuboid would block us even after stepping up
        return null;
      }
    }

    return highestStep;
  }

  /**
   * Check if there's headroom for the character at a position
   * @param {number} x - X position
   * @param {number} y - Y position (feet)
   * @param {number} z - Z position
   * @param {Character} character - The character
   * @returns {boolean} True if there's enough headroom
   */
  hasHeadroom(x, y, z, character) {
    const halfWidth = character.width / 2;
    const halfDepth = character.depth / 2;

    const queryBox = {
      minX: x - halfWidth + GroundDetector.EPSILON,
      minY: y + GroundDetector.EPSILON,
      minZ: z - halfDepth + GroundDetector.EPSILON,
      maxX: x + halfWidth - GroundDetector.EPSILON,
      maxY: y + character.height - GroundDetector.EPSILON,
      maxZ: z + halfDepth - GroundDetector.EPSILON
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.) - head can pass through
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidAABB = {
        minX: cuboid.x,
        minY: cuboid.y,
        minZ: cuboid.z,
        maxX: cuboid.x + cuboid.width,
        maxY: cuboid.y + (cuboid.collisionHeight || cuboid.height),
        maxZ: cuboid.z + cuboid.depth
      };

      // Check actual intersection
      if (
        queryBox.minX < cuboidAABB.maxX && queryBox.maxX > cuboidAABB.minX &&
        queryBox.minY < cuboidAABB.maxY && queryBox.maxY > cuboidAABB.minY &&
        queryBox.minZ < cuboidAABB.maxZ && queryBox.maxZ > cuboidAABB.minZ
      ) {
        return false; // Blocked by ceiling
      }
    }

    return true;
  }

  /**
   * Find the ground height at a position
   * @param {number} x - X position
   * @param {number} z - Z position
   * @param {number} width - Width to check
   * @param {number} depth - Depth to check
   * @param {number} startY - Y position to search downward from
   * @param {number} maxDropDistance - Maximum distance to search downward
   * @returns {number|null} Ground Y position or null if no ground found
   */
  findGroundHeight(x, z, width, depth, startY, maxDropDistance = 100) {
    const halfWidth = width / 2;
    const halfDepth = depth / 2;

    const queryBox = {
      minX: x - halfWidth,
      minY: startY - maxDropDistance,
      minZ: z - halfDepth,
      maxX: x + halfWidth,
      maxY: startY,
      maxZ: z + halfDepth
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    let groundY = null;

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.) - can't stand on them
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidTop = cuboid.y + cuboid.height;

      // Check horizontal overlap
      if (this.horizontalOverlapAt(x, z, width, depth, cuboid)) {
        // Ground must be at or below our current position
        if (cuboidTop <= startY + GroundDetector.EPSILON) {
          if (groundY === null || cuboidTop > groundY) {
            groundY = cuboidTop;
          }
        }
      }
    }

    return groundY;
  }
}
